import { useEffect, useState } from "react";
import axios from "axios";
import { ArrowLeft, Lock, MessageCircle, RefreshCw } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
// Same key as /admin, so logging in on one page also unlocks the other.
const TOKEN_KEY = "lurio_admin_token";
const STATUSES = ["pending", "confirmed", "shipped", "delivered", "refused", "cancelled"];

// Orders saved before statuses existed carry an old value; show them as pending.
const statusOf = (order) => (STATUSES.includes(order.status) ? order.status : "pending");

// Customers type Moroccan numbers in several ways (06 12 34 56 78, 0612345678, +212612345678). wa.me wants digits with the country code.
const waHref = (phone) => {
  let d = phone.replace(/\D/g, "");
  if (d.startsWith("00")) d = d.slice(2);
  else if (d.startsWith("0")) d = `212${d.slice(1)}`;
  else if (d.length === 9) d = `212${d}`;
  return `https://wa.me/${d}`;
};

const formatDate = (iso) => new Date(iso).toLocaleString("en-GB", { dateStyle: "medium", timeStyle: "short" });

const OrderRow = ({ order, onStatus }) => (
  <li data-testid={`order-row-${order.id}`} className="paper-card grid gap-3 p-4 sm:grid-cols-[1fr_auto] sm:items-center">
    <div className="min-w-0">
      <p className="font-bold text-ink">{order.full_name}</p>
      <p className="mt-0.5 text-xs text-ink-soft">{formatDate(order.created_at)}</p>
      <p className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-sm">
        <a href={waHref(order.phone)} target="_blank" rel="noreferrer" data-testid={`order-phone-${order.id}`} className="inline-flex min-h-[44px] items-center gap-1.5 font-bold text-blue underline-offset-2 hover:underline sm:min-h-0">
          <MessageCircle size={16} className="text-[#25D366]" /> {order.phone}
        </a>
        <span className="text-ink">{order.city}</span>
      </p>
      <p className="mt-1 text-sm font-semibold text-ink">
        {order.quantity} × {order.unit_price} MAD = <span className="text-blue">{order.total} MAD</span>
      </p>
    </div>
    <select
      value={statusOf(order)}
      onChange={(e) => onStatus(order, e.target.value)}
      aria-label={`Status of ${order.full_name}'s order`}
      data-testid={`order-status-${order.id}`}
      className="field sm:w-44"
    >
      {STATUSES.map((s) => (
        <option key={s} value={s}>{s}</option>
      ))}
    </select>
  </li>
);

export default function AdminOrders() {
  const [token, setToken] = useState(() => sessionStorage.getItem(TOKEN_KEY) || "");
  const [orders, setOrders] = useState(null);
  const [err, setErr] = useState("");
  const [note, setNote] = useState("");
  const [loading, setLoading] = useState(false);
  const authed = orders !== null;

  const load = async (tk) => {
    setLoading(true);
    setErr("");
    try {
      const { data } = await axios.get(`${API}/orders`, { headers: { "X-Admin-Token": tk } });
      sessionStorage.setItem(TOKEN_KEY, tk);
      setOrders(data);
    } catch (e) {
      if (e.response?.status === 401) {
        sessionStorage.removeItem(TOKEN_KEY);
        setOrders(null);
        setErr("Wrong admin token.");
      } else {
        setErr("Could not load the orders. Is the backend running?");
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (token) load(token);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const changeStatus = async (order, status) => {
    const previous = order.status;
    setNote("");
    setOrders((list) => list.map((o) => (o.id === order.id ? { ...o, status } : o)));
    try {
      await axios.patch(`${API}/orders/${order.id}/status`, { status }, { headers: { "X-Admin-Token": token } });
      setNote(`${order.full_name}: ${status}`);
    } catch {
      setOrders((list) => list.map((o) => (o.id === order.id ? { ...o, status: previous } : o)));
      setNote("Could not save the status. Nothing was changed.");
    }
  };

  const counts = authed ? STATUSES.map((s) => [s, orders.filter((o) => statusOf(o) === s).length]).filter(([, n]) => n > 0) : [];

  return (
    <main dir="ltr" lang="en" className="min-h-screen bg-cream px-5 py-10 sm:px-8" data-testid="admin-orders-page">
      <div className="mx-auto max-w-4xl">
        <a href="/" className="inline-flex items-center gap-2 text-sm font-bold text-blue"><ArrowLeft size={16} /> Back to the page</a>
        <h1 className="font-serif mt-4 text-4xl font-semibold text-ink">Orders</h1>
        {!authed ? (
          <form onSubmit={(e) => { e.preventDefault(); load(token); }} className="paper-card mt-8 flex max-w-md flex-col gap-3 p-6" data-testid="admin-orders-login-form">
            <label htmlFor="orders-token" className="flex items-center gap-2 text-sm font-bold text-ink"><Lock size={16} /> Admin token</label>
            <input id="orders-token" type="password" value={token} onChange={(e) => setToken(e.target.value)} className="field" data-testid="admin-orders-token-input" autoComplete="current-password" />
            {err && <p role="alert" className="text-sm font-bold text-coral" data-testid="admin-orders-login-error">{err}</p>}
            <button type="submit" disabled={loading} className="min-h-[48px] rounded-full bg-coral font-bold text-cream disabled:opacity-60" data-testid="admin-orders-login-button">Log in</button>
          </form>
        ) : (
          <>
            <div className="mt-3 flex flex-wrap items-center gap-3">
              <p data-testid="admin-orders-count" className="text-sm font-bold text-ink">
                {orders.length} {orders.length === 1 ? "order" : "orders"}
                {counts.length > 0 && <span className="font-semibold text-ink-soft"> · {counts.map(([s, n]) => `${n} ${s}`).join(" · ")}</span>}
              </p>
              <button type="button" onClick={() => load(token)} disabled={loading} data-testid="admin-orders-refresh" className="inline-flex min-h-[44px] items-center gap-2 rounded-full border border-ink/20 px-4 text-sm font-bold text-ink hover:bg-cream-deep disabled:opacity-60">
                <RefreshCw size={14} className={loading ? "animate-spin" : ""} /> Refresh
              </button>
            </div>
            <p role="status" data-testid="admin-orders-note" className="mt-2 min-h-[1.25rem] text-sm font-bold text-coral">{note || err}</p>
            {orders.length === 0 ? (
              <p className="paper-card mt-4 p-6 text-ink-soft" data-testid="admin-orders-empty">No orders yet.</p>
            ) : (
              <ul className="mt-4 space-y-3">
                {orders.map((o) => (
                  <OrderRow key={o.id} order={o} onStatus={changeStatus} />
                ))}
              </ul>
            )}
          </>
        )}
      </div>
    </main>
  );
}
